import { Locale } from 'enums/locale';
import { NextParsedUrlQuery } from 'next/dist/server/request-meta';
import getT from 'next-translate/getT';

/**
 * Determines if SEO content should be hidden based on pagination parameters
 * @param query - The query parameters from the request
 * @param locale - Current locale
 * @param locales - Available locales from router
 * @param params - Route parameters to exclude from query analysis
 * @returns Object with hideSeoContent flag and additional info
 */
export async function shouldHideSeoContentForPagination(
  query: NextParsedUrlQuery,
  locale: Locale,
  locales: string[],
  params?: NextParsedUrlQuery
): Promise<{
  hideSeoContent: boolean;
  hasPaginationParam: boolean;
  currentPage: number;
}> {
  const allPossiblePageParams: string[] = [];
  for (const availableLocale of locales) {
    const t = await getT(availableLocale, 'common');
    const pageParam = t('pageQueryParam');
    if (!allPossiblePageParams.includes(pageParam)) {
      allPossiblePageParams.push(pageParam);
    }
  }

  const filteredQuery = { ...query };
  if (params) {
    for (const key of Object.keys(params)) {
      delete filteredQuery[key];
    }
  }

  const existingPageParam = allPossiblePageParams.find(
    (paramName) => filteredQuery[paramName] !== undefined
  );

  const hasPaginationParam = !!existingPageParam;
  const currentPage = existingPageParam
    ? parseInt(String(filteredQuery[existingPageParam]), 10) || 1
    : 1;

  const hideSeoContent = hasPaginationParam && currentPage > 1;

  return {
    hideSeoContent,
    hasPaginationParam,
    currentPage,
  };
}

export async function shouldNoIndexPaginatedPage(
  query: NextParsedUrlQuery,
  locale: Locale,
  locales: string[],
  params?: NextParsedUrlQuery
): Promise<boolean> {
  const { hasPaginationParam } = await shouldHideSeoContentForPagination(
    query,
    locale,
    locales,
    params
  );

  const queryForNoIndex = { ...query };
  if (params) {
    for (const key of Object.keys(params)) {
      delete queryForNoIndex[key];
    }
  }

  const allPossiblePageParams: string[] = [];
  for (const availableLocale of locales) {
    const t = await getT(availableLocale, 'common');
    const pageParam = t('pageQueryParam');
    if (!allPossiblePageParams.includes(pageParam)) {
      allPossiblePageParams.push(pageParam);
    }
  }

  for (const pageParam of allPossiblePageParams) {
    delete queryForNoIndex[pageParam];
  }

  return hasPaginationParam && Object.keys(queryForNoIndex).length > 0;
}

/**
 * Determines if all SEO links (canonical, alternate, prev, next) should be removed
 * This applies to noindex pages that have multiple query parameters
 * @param query - The query parameters from the request
 * @param locale - Current locale
 * @param locales - Available locales from router
 * @param params - Route parameters to exclude from query analysis
 * @returns Boolean indicating if SEO links should be removed
 */
export async function shouldRemoveAllSeoLinks(
  query: NextParsedUrlQuery,
  locale: Locale,
  locales: string[],
  params?: NextParsedUrlQuery
): Promise<boolean> {
  // This is the same logic as shouldNoIndexPaginatedPage
  // If a page is noindexed due to multiple query params, remove all SEO links
  return await shouldNoIndexPaginatedPage(query, locale, locales, params);
}
